import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react'

import * as Y from 'yjs'
import { useReactiveStore, useReactiveStoreEvent } from '@verbit-ai/frontend-common'

import {
    getRtBrokerWsServerUrl,
    getTranscriptJobId,
    getTranscriptServerUrl,
    getTranscriptionDocumentId,
    isMockActive,
    isMockAutoRefreshRequested,
    isPdfDisplayMode,
} from 'src/utils/env'
import { getLanguageByCode } from 'src/utils/language'
import { analyticsService } from 'src/services/AnalyticsService'
import { asrTelemetryService } from 'src/services/AsrTelemetryService'
import { rollbarService } from 'src/services/RollbarService'
import { platformHttpClient, visorHttpClient } from 'src/network'
import { StreamClient, StreamSourceInterface } from 'src/network/StreamClient'
import { useLanguage } from 'src/contexts/LanguageContext'
import { useQueue } from 'src/contexts/QueueProvider'
import { StreamMockSource } from 'src/mocks/StreamMockSource'
import { AblyStreamSource } from 'src/network/AblyStreamSource'
import { useFeatureFlag, useFeatureFlagsAvailable } from 'src/contexts/FeatureFlagContext'
import { DummyTranscriptClient, TranscriptClient, TranscriptSubscriptionCallback } from 'src/models/TranscriptClient'
import { AblyTranscriptClient } from 'src/network/AblyTranscriptClient'
import { TranscriptOld } from 'src/models/TranscriptOld'
import { RtBrokerTranscriptClientV2 } from 'src/network/RtBrokerTranscriptClientV2'
import { DocumentTranscriptClient } from 'src/network/DocumentTranscriptClient'
import { TranscriptParagraph, TranscriptSpeaker, TranscriptTimings, WordTiming } from 'src/models/Transcript'
import { TranscriptUserState, TranscriptUserStateCallback } from 'src/models/TranscriptUserState'
import { TranscriptConnectionState, DEFAULT_TRANSCRIPT_CONNECTION_STATE } from 'src/models/TranscriptConnectionState'
import type { TranscriptCrdtTransport, TranscriptCrdtTransportKind } from 'src/network/TranscriptCrdtTransport'
import { AblyCrdtTransport } from 'src/network/AblyCrdtTransport'
import { WebSocketCrdtTransport } from 'src/network/WebSocketCrdtTransport'
import { TranscriptDocCache, buildCacheKey } from 'src/services/TranscriptDocCache'
import { useShallowMemo } from 'src/hooks/useShallowMemo'
import { useVariableRef } from 'src/hooks/useVariableRef'

import { MetadataContext, useSetIsJobFinished, useSetShouldUseInsightsFallback, useTranscriptMetadata } from './MetadataContext'
import { useTranscriptError } from './TranscriptErrorContext'
import { isInsightsFallbackEligible } from './insightsFallback'

/**
 * Represents the scope of the current transcript / document.
 * If the scope changes, it means that the transcript client was swapped. This is
 * especially important for detecting connection drops since every language is treated as a separate transcript.
 * If we didn't do this, a language change could be interpreted as a connection drop for example.
 *
 * Patterns are:
 *   - 'dummy'                                 // no real provider mounted yet
 *   - `ably:${eventId}:${languageCode}`       // TranscriptProviderOld
 *   - `rt-broker:${jobId}:${languageCode}`    // TranscriptProviderNew
 *   - `document:${transcriptionDocumentId}`   // DocumentTranscriptProvider
 */
type TranscriptConnectionScope = 'dummy' | `ably:${string}:${string}` | `rt-broker:${string}:${string}` | `document:${string}`

type TranscriptContextValue = {
    client: TranscriptClient
    scope: TranscriptConnectionScope
}

const DEFAULT_TRANSCRIPT_CONTEXT_VALUE: TranscriptContextValue = {
    client: new DummyTranscriptClient(),
    scope: 'dummy',
}

export const TranscriptContext = createContext<TranscriptContextValue>(DEFAULT_TRANSCRIPT_CONTEXT_VALUE)

export type TranscriptProviderProps = PropsWithChildren<{
    eventId: string
}>

const streamClient = new StreamClient()
const LIVE_EVENTS = ['trax', 'asr']

const TranscriptProviderOld = (props: TranscriptProviderProps) => {
    const { children, eventId } = props

    const [transcriptClient, setTranscriptClient] = useState<DummyTranscriptClient | AblyTranscriptClient>(
        new DummyTranscriptClient({
            initialConnectionState: DEFAULT_TRANSCRIPT_CONNECTION_STATE,
        }),
    )

    const metadata = useTranscriptMetadata()!
    const { transcriptErrorHandler } = useTranscriptError()
    const setIsCurrentJobFinished = useSetIsJobFinished()
    const isAblyVerboseLogsEnabled = useFeatureFlag('ably_verbose_logs')

    const { requestedLanguageCode, requestedLanguageName, translatedLanguageCode } = useLanguage()

    const executeAsrQueue = useCallback(() => {
        const asrMessages = streamClient.queue('asr_chunk').drain()
        const outcome = transcriptClient.processAsrQueue(asrMessages)
        if (outcome) {
            asrTelemetryService.recordDrainOutcome(outcome)
        }
    }, [transcriptClient])

    const setAsrQueueSize = useQueue('asr', executeAsrQueue)

    const streamSourceRef = useRef<StreamSourceInterface>(undefined)

    useEffect(() => {
        if (streamSourceRef.current) {
            streamSourceRef.current.unsubscribe(...LIVE_EVENTS)
        }

        const newTranscriptClient = new AblyTranscriptClient({
            requestedLanguage: requestedLanguageName,
        })
        setTranscriptClient(newTranscriptClient)

        platformHttpClient
            .getTranscript(eventId, translatedLanguageCode)
            .then(async (initialTranscript) => {
                if (isMockActive() && document.mocks?.getMode() === 'replay') {
                    streamSourceRef.current = new StreamMockSource({
                        delayConfig: {
                            default: [400, 500],
                        },
                    })
                    if (isMockAutoRefreshRequested()) {
                        ;(streamSourceRef.current as unknown as StreamMockSource).onStreamFinished = () => {
                            setIsCurrentJobFinished(true)
                        }
                    }
                } else if (metadata.sessionId && metadata.ablyTokenUrl) {
                    streamSourceRef.current = new AblyStreamSource({
                        url: metadata.ablyTokenUrl,
                        options: metadata.ablyClientOptions,
                        sessionId: metadata.sessionId.toString(),
                        translatedLanguageCode,
                        verbose: isAblyVerboseLogsEnabled,
                    })
                }

                if (streamSourceRef.current) {
                    streamClient.attachSource(streamSourceRef.current)
                }

                const start = TranscriptOld.getProcessedTime(initialTranscript)

                const getAsrHistoryStartTime = performance.now()
                const asrHistory = await streamClient.history<'asr_chunk'>('asr', { start, limit: 1000 })
                const getAsrHistoryEndTime = performance.now()
                analyticsService.track('Performance - Get ASR history', {
                    responseTime: getAsrHistoryEndTime - getAsrHistoryStartTime,
                    messagesAmount: asrHistory.length,
                })

                newTranscriptClient.processAsrHistory(initialTranscript, asrHistory)

                streamClient.subscribe(...LIVE_EVENTS)
                streamClient.queue('asr_chunk').on('size_change', setAsrQueueSize)
                streamClient.on('asr_chunk', asrTelemetryService.recordMessage)
                streamClient.queue('asr_chunk').on('size_change', asrTelemetryService.recordQueueDepth)
                asrTelemetryService.start()
            })
            .catch(transcriptErrorHandler)

        return () => {
            asrTelemetryService.stop()
            streamClient.off('asr_chunk', asrTelemetryService.recordMessage)
            streamClient.queue('asr_chunk').off('size_change', asrTelemetryService.recordQueueDepth)
            streamSourceRef.current?.unsubscribe(...LIVE_EVENTS)
            streamClient.queue('asr_chunk').off('size_change', setAsrQueueSize)
            transcriptClient.destroy()
        }
        // this effect should not be re-run when metadata changes
        // (because it would re-create Ably stream source every 2 minutes)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId, translatedLanguageCode, setAsrQueueSize, setIsCurrentJobFinished, transcriptErrorHandler])

    const transcriptContextValue = useShallowMemo<TranscriptContextValue>({
        client: transcriptClient,
        scope: `ably:${eventId}:${requestedLanguageCode}`,
    })

    return <TranscriptContext.Provider value={transcriptContextValue}>{children}</TranscriptContext.Provider>
}

type TransportFactoryContext = {
    doc: Y.Doc
    jobId: string
    languageCode: string
    verbose?: boolean
}

const TRANSPORT_FACTORIES: Record<TranscriptCrdtTransportKind, (context: TransportFactoryContext) => TranscriptCrdtTransport> = {
    'ably-crdt': ({ doc, jobId, languageCode, verbose }) => {
        const ablyTokenUrl = `${getTranscriptServerUrl()}/api/v2/transcript/${jobId}/${languageCode}/ably-token`

        return new AblyCrdtTransport({ doc, jobId, languageCode, ablyTokenUrl, verbose })
    },
    websocket: ({ doc, jobId, languageCode }) =>
        new WebSocketCrdtTransport({ doc, jobId, languageCode, baseWsUrl: getRtBrokerWsServerUrl() }),
}

type CreateTranscriptClientOptions = {
    transportKind: TranscriptCrdtTransportKind
    jobId: string
    languageCode: string
    isCacheEnabled: boolean
    verbose?: boolean
}

/**
 * Builds the per-session Y.Doc + transport + client graph. The client's observers are attached at
 * construction so any cached `applyUpdate` during `init()` still reaches `_transcript` and
 * subscribers. Live jobs always use the v2 projection (`RtBrokerTranscriptClientV2`). Cached v1
 * sessions under this key are discarded via `CURRENT_SCHEMA_VERSION` 2.
 */
const createTranscriptClient = ({
    transportKind,
    jobId,
    languageCode,
    isCacheEnabled,
    verbose,
}: CreateTranscriptClientOptions): TranscriptClient => {
    const doc = new Y.Doc()

    const transport = TRANSPORT_FACTORIES[transportKind]({
        doc,
        jobId,
        languageCode,
        verbose,
    })

    const cache = isCacheEnabled ? new TranscriptDocCache({ key: buildCacheKey({ jobId, languageCode }), doc }) : null

    return new RtBrokerTranscriptClientV2({ transport, doc, cache, language: getLanguageByCode(languageCode) })
}

const TranscriptProviderNew = (props: TranscriptProviderProps) => {
    const { children } = props

    const { requestedLanguageCode } = useLanguage()
    const transcriptJobId = getTranscriptJobId()
    const { transcriptErrorHandler, setTranscriptError } = useTranscriptError()
    const setShouldUseInsightsFallback = useSetShouldUseInsightsFallback()
    const metadata = useTranscriptMetadata()
    const isAblyVerboseLogsEnabled = useFeatureFlag('ably_verbose_logs')
    const isAblyVerboseLogsEnabledRef = useVariableRef(isAblyVerboseLogsEnabled)
    const transportKind: TranscriptCrdtTransportKind = 'ably-crdt'
    // Flag gates the IndexedDB doc cache. Mock mode also bypasses it so replayed sessions start from a clean Y.Doc.
    const isCacheEnabled = useFeatureFlag('transcript_doc_cache') && !isMockActive()

    const [transcriptClient, setTranscriptClient] = useState<TranscriptClient>(
        () =>
            new DummyTranscriptClient({
                initialConnectionState: DEFAULT_TRANSCRIPT_CONNECTION_STATE,
            }),
    )

    useEffect(() => {
        if (!requestedLanguageCode) return

        if (!transcriptJobId) return

        setShouldUseInsightsFallback(false)

        const client = createTranscriptClient({
            transportKind,
            jobId: transcriptJobId,
            languageCode: requestedLanguageCode,
            isCacheEnabled,
            verbose: isAblyVerboseLogsEnabledRef.current,
        })

        setTranscriptClient(client)

        client.init().catch((error) => {
            if (!metadata) {
                return
            }

            if (isInsightsFallbackEligible({ error, metadata })) {
                client.getConnectionState().setConnectionState({
                    status: 'connected',
                    isInitialized: true,
                })
                setShouldUseInsightsFallback(true)
                setTranscriptError(null)

                return
            }

            console.error('Failed to initialize RT-Broker client:', error)

            rollbarService.error('RT-Broker initialization failed')

            transcriptErrorHandler(error)
        })

        return () => {
            client.destroy()
        }
    }, [
        requestedLanguageCode,
        transcriptJobId,
        transcriptErrorHandler,
        metadata,
        setShouldUseInsightsFallback,
        setTranscriptError,
        transportKind,
        isCacheEnabled,
        isAblyVerboseLogsEnabledRef,
    ])

    const transcriptContextValue = useShallowMemo<TranscriptContextValue>({
        client: transcriptClient,
        scope: `rt-broker:${transcriptJobId}:${requestedLanguageCode}`,
    })

    return <TranscriptContext.Provider value={transcriptContextValue}>{children}</TranscriptContext.Provider>
}

const DocumentTranscriptProvider = (props: TranscriptProviderProps) => {
    const { children } = props

    const transcriptionDocumentId = getTranscriptionDocumentId()
    const { transcriptErrorHandler } = useTranscriptError()

    const [transcriptClient, setTranscriptClient] = useState<TranscriptClient>(() => {
        return new DummyTranscriptClient({
            initialConnectionState: DEFAULT_TRANSCRIPT_CONNECTION_STATE,
        })
    })

    useEffect(() => {
        if (!transcriptionDocumentId) {
            return
        }

        const newTranscriptClient = new DocumentTranscriptClient({
            transcriptionDocumentId,
            visorHttpClient,
        })

        setTranscriptClient(newTranscriptClient)
        newTranscriptClient.init().catch((error) => {
            console.error('Failed to initialize Document client:', error)
            rollbarService.error('Document initialization failed')

            transcriptErrorHandler(error)
        })

        return () => {
            newTranscriptClient.destroy()
        }
    }, [transcriptionDocumentId, transcriptErrorHandler])

    const transcriptContextValue = useShallowMemo<TranscriptContextValue>({
        client: transcriptClient,
        scope: `document:${transcriptionDocumentId}`,
    })

    return <TranscriptContext.Provider value={transcriptContextValue}>{children}</TranscriptContext.Provider>
}

export const TranscriptProvider = (props: TranscriptProviderProps) => {
    const { eventId, children } = props
    const areFeatureFlagsAvailable = useFeatureFlagsAvailable()
    const metadata = useTranscriptMetadata()

    if (!areFeatureFlagsAvailable) {
        return <>{children}</>
    }

    // The live path is unconditionally the v2 RtBroker client (`TranscriptProviderNew`); mock/demo
    // sessions still ride the legacy stack (`TranscriptProviderOld`), reworked/removed in LG-2940/LG-2941.
    const viewMode = metadata?.viewMode
    const Provider = viewMode === 'document' ? DocumentTranscriptProvider : isMockActive() ? TranscriptProviderOld : TranscriptProviderNew

    return <Provider eventId={eventId}>{children}</Provider>
}

export const useTranscriptConnectionScope = (): TranscriptConnectionScope => {
    const transcriptContext = useContext(TranscriptContext)

    if (transcriptContext === undefined) {
        throw new Error('You forgot to use <TranscriptContext.Provider />, shame on you.')
    }

    return transcriptContext.scope
}

export const useIsTranscriptAvailable = () => {
    const transcriptContext = useContext(TranscriptContext)
    const metadata = useContext(MetadataContext)

    if (transcriptContext === undefined || metadata === undefined) {
        throw new Error('You forgot to use <TranscriptProvider />, shame on you.')
    }

    const transcript = transcriptContext.client

    return !!(transcript && metadata)
}

export const useIsTranscriptEmpty = () => {
    const transcript = useTranscriptClient()
    const metadata = useContext(MetadataContext)

    if (transcript === undefined || metadata === undefined) {
        throw new Error('You forgot to use <TranscriptProvider />, shame on you.')
    }

    // PDF mode is driven by `metadata.fileUrl` (React context, so re-renders
    // re-read `getSnapshot` automatically and no subscription is needed).
    // Live mode subscribes to `'paragraphIds'`; `useSyncExternalStore` also
    // re-reads on `transcript` identity flips (e.g. cache-hydrate client swap).
    const fileUrl = metadata?.fileUrl
    const subscribe = useCallback(
        (onChange: () => void) => {
            if (isPdfDisplayMode()) return () => {}

            return transcript.subscribe('paragraphIds', onChange)
        },
        [transcript],
    )
    const getSnapshot = useCallback(() => {
        if (isPdfDisplayMode()) return !fileUrl

        return transcript.isEmpty()
    }, [transcript, fileUrl])

    return useSyncExternalStore(subscribe, getSnapshot)
}

/**
 * Hook to get the current active transcript client.
 * **DOES NOT** reactively subscribe to any changes.
 */
export const useTranscriptClient = () => {
    const transcriptContext = useContext(TranscriptContext)

    if (transcriptContext === undefined) {
        throw new Error('You forgot to use <TranscriptContext.Provider />, shame on you.')
    }

    return transcriptContext.client
}

/**
 * Hook to subscribe to all transcript changes under a given path using a callback.
 */
export const useTranscriptEvent = (path: string, cb: TranscriptSubscriptionCallback) => {
    const transcriptClient = useTranscriptClient()

    useEffect(() => {
        return transcriptClient.subscribe(path, cb)
    }, [transcriptClient, path, cb])
}

/**
 * Hook to reactively subscribe to all transcript changes under the given path.
 * Returns the most up-to-date value of the given path.
 */
export const useTranscriptSelector = <T,>(path: string) => {
    const transcriptClient = useTranscriptClient()

    return useSyncExternalStore<T>(
        (onChange) => transcriptClient.subscribe(path, onChange),
        () => transcriptClient.toJS(path)!,
    )
}

export const useTranscriptParagraphIds = () => {
    return useTranscriptSelector<string[]>('paragraphIds')
}

export const useTranscriptParagraph = (paragraphId: string) => {
    return useTranscriptSelector<TranscriptParagraph>(`paragraphs.${paragraphId}`)
}

export const useTranscriptSpeaker = (speakerId: string) => {
    return useTranscriptSelector<TranscriptSpeaker>(`speakers.${speakerId}`)
}

export const useTranscriptSpeakers = () => {
    const transcriptClient = useTranscriptClient()
    useTranscriptSelector('speakers') // use as subscription

    return transcriptClient.getSpeakers()
}

export const useTranscriptTimings = () => {
    return useTranscriptSelector<TranscriptTimings>('timings')
}

export const useParagraphTimings = (paragraphId: string) => {
    return useTranscriptSelector<WordTiming[]>(`timings.${paragraphId}`)
}

/**
 * Hook to subscribe to all user state changes via callback.
 */
export const useTranscriptUserStateEvent = (cb: TranscriptUserStateCallback) => {
    const transcriptClient = useTranscriptClient()
    const userStateManager = transcriptClient.getUserState()

    useEffect(() => {
        return userStateManager.subscribe(cb)
    }, [userStateManager, cb])
}

/**
 * Returns the local user state.
 */
export const useTranscriptLocalUserState = () => {
    const transcriptClient = useTranscriptClient()
    const userStateManager = transcriptClient.getUserState()

    return useSyncExternalStore<TranscriptUserState>(
        (onChange) => userStateManager.subscribe(onChange),
        () => userStateManager.getLocalState(),
    )
}

/**
 * Returns the remote user states.
 * Note: This is a placeholder to show how remote user states can work.
 */
export const useTranscriptRemoteUserStates = () => {
    const transcriptClient = useTranscriptClient()
    const userStateManager = transcriptClient.getUserState()

    return useSyncExternalStore<TranscriptUserState[]>(
        (onChange) => userStateManager.subscribe(onChange),
        () => userStateManager.getRemoteStates(),
    )
}

export const useTranscriptUserStateManager = () => {
    const transcriptClient = useTranscriptClient()
    const userStateManager = transcriptClient.getUserState()

    return userStateManager
}

export const useTranscriptConnectionState = () => {
    const transcriptClient = useTranscriptClient()

    return useReactiveStore(transcriptClient.getConnectionState())
}
export const useTranscriptConnectionStateEvent = (cb: (state: TranscriptConnectionState) => void) => {
    const transcriptClient = useTranscriptClient()
    const connectionStateManager = transcriptClient.getConnectionState()

    return useReactiveStoreEvent(connectionStateManager, cb)
}
